import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  flow_m3hr: '',
  duty: 'feed_discharge',
  target_velocity_m_s: '',
  steam_mass_flow_kg_hr: '',
  steam_pressure_barg: '',
};

const DUTY_OPTIONS = [
  { value: 'feed_discharge', label: 'Pump Discharge / Process Feed', hint: '2.0 m/s' },
  { value: 'gravity_suction', label: 'Gravity Flow / Pump Suction', hint: '1.2 m/s' },
  { value: 'backwash', label: 'Backwash / High-Flow Intermittent Line', hint: '2.5 m/s' },
  { value: 'steam', label: 'Saturated Steam Line', hint: '30 m/s' },
];

const PipeSizingCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const isSteam = form.duty === 'steam';

  const buildInputs = () => {
    if (isSteam) {
      if (!form.steam_mass_flow_kg_hr || form.steam_pressure_barg === '') {
        showToast('Steam mass flow and steam pressure are required', 'error');
        return null;
      }
      return {
        duty: form.duty,
        steam_mass_flow_kg_hr: Number(form.steam_mass_flow_kg_hr),
        steam_pressure_barg: Number(form.steam_pressure_barg),
        target_velocity_m_s: form.target_velocity_m_s === '' ? null : Number(form.target_velocity_m_s),
      };
    }
    if (!form.flow_m3hr) {
      showToast('Flow is required', 'error');
      return null;
    }
    return {
      flow_m3hr: Number(form.flow_m3hr),
      duty: form.duty,
      target_velocity_m_s: form.target_velocity_m_s === '' ? null : Number(form.target_velocity_m_s),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('pipe_sizing', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'pipe_sizing',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Pipe Sizing</h1>
        <p className="text-xs text-gray-500 mb-6">
          Recommends a required pipe internal diameter from flow ÷ target velocity — the raw
          diameter only, no rounding to a standard nominal-bore size (pick the actual pipe size
          yourself). Steam duty takes mass flow + pressure instead of a volumetric flow, since
          steam is compressible. Once you've picked a size, check it with the Pipe Pressure Drop
          calculator.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duty *</label>
              <div className="space-y-2">
                {DUTY_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      checked={form.duty === opt.value}
                      onChange={() => setForm((f) => ({ ...f, duty: opt.value }))}
                    />
                    {opt.label} <span className="text-xs text-gray-400">(default {opt.hint})</span>
                  </label>
                ))}
              </div>
            </div>

            {isSteam ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Steam Mass Flow (kg/hr) *</label>
                  <input
                    required
                    type="number"
                    step="any"
                    value={form.steam_mass_flow_kg_hr}
                    onChange={handleChange('steam_mass_flow_kg_hr')}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Steam Pressure (barg) *</label>
                  <input
                    required
                    type="number"
                    step="any"
                    value={form.steam_pressure_barg}
                    onChange={handleChange('steam_pressure_barg')}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flow (m³/hr) *</label>
                <input
                  required
                  type="number"
                  step="any"
                  value={form.flow_m3hr}
                  onChange={handleChange('flow_m3hr')}
                  className="w-48 border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Velocity Override (m/s)</label>
              <input
                type="number"
                step="any"
                value={form.target_velocity_m_s}
                onChange={handleChange('target_velocity_m_s')}
                className="w-48 border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="Blank = duty default"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="submit"
                disabled={isCalculating}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Stat label="Duty" value={result.outputs.duty_label} />
              <Stat label="Target Velocity" value={`${result.outputs.target_velocity_m_s} m/s${result.outputs.velocity_is_default ? ' (default)' : ''}`} />
              <Stat label="Required Internal Diameter" value={`${result.outputs.required_internal_diameter_mm} mm`} highlight />
              {result.outputs.duty === 'steam' && (
                <>
                  <Stat label="Steam Pressure (absolute)" value={`${result.outputs.steam_pressure_bara} bara`} />
                  <Stat label="Saturation Temperature" value={`${result.outputs.steam_saturation_temp_c} °C`} />
                  <Stat label="Specific Volume (vg)" value={`${result.outputs.steam_specific_volume_m3_kg} m³/kg`} />
                  <Stat label="Volumetric Flow (derived)" value={`${result.outputs.flow_m3hr} m³/hr`} />
                </>
              )}
            </div>

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-xs font-semibold text-komal-blue hover:underline mb-3"
                >
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Calculation Steps
                        </h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-0">
                                  <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{step.step}</td>
                                  <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                  <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Assumptions
                        </h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => clearResult()}
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default PipeSizingCalculatorPage;
